export const CONTEXT_INVOICE_CLIENT = "invoice_client";
export const CONTEXT_INVOICE_COMMISSION = "invoice_commission";

/** YYYY-MM-DD in local timezone for `<input type="date" />`. */
export function getLocalDateInputValue(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function permOn(permissions, key) {
  const v = permissions?.[key];
  return v === 1 || v === "1" || v === true;
}

export function resolveInvoiceFollowupContext(invoice) {
  const type = String(invoice?.invoice_type || invoice?.invoiceType || "")
    .trim()
    .toLowerCase();
  const source = String(
    invoice?.invoice_source_module || invoice?.invoiceSourceModule || "",
  )
    .trim()
    .toLowerCase();
  if (
    ["commission", "bonus", "commission_bonus"].includes(type) ||
    source === "new_commission"
  ) {
    return CONTEXT_INVOICE_COMMISSION;
  }
  return CONTEXT_INVOICE_CLIENT;
}

export function hasInvoiceFollowupPermission(contextType, permissions = {}) {
  if (contextType === CONTEXT_INVOICE_CLIENT) {
    return (
      permOn(permissions, "all_invoice_client_followups") ||
      permOn(permissions, "my_invoice_client_followups") ||
      permOn(permissions, "all_followups")
    );
  }
  return (
    permOn(permissions, "all_invoice_commission_followups") ||
    permOn(permissions, "my_invoice_commission_followups") ||
    permOn(permissions, "all_followups")
  );
}

export const INVOICE_FOLLOWUP_NOTE_ACTIONS = new Set([
  "followup_assigned",
  "followup_completed",
  "followup_rescheduled",
  "followup_reassigned",
]);

export function buildCommissionDetailReturnQuery(params = {}) {
  const q = new URLSearchParams();
  if (params.studentId) q.set("student_id", params.studentId);
  if (params.appId) q.set("app_id", params.appId);
  if (params.providerId) q.set("provider_id", params.providerId);
  if (params.courseId) q.set("course_id", params.courseId);
  if (params.applicationCourseId) {
    q.set("application_course_id", params.applicationCourseId);
  }
  q.set("type", params.type || "education");
  const qs = q.toString();
  return qs ? `/commissions/detail?${qs}` : "/commissions/detail";
}

export function buildFinanceInvoiceEditPath(clientId, invoiceId) {
  if (!clientId || !invoiceId) return "/finance/invoices";
  return `/finance/edit-invoice/${clientId}/${invoiceId}`;
}

export function buildCommissionInvoiceEditPath(clientId, invoiceId) {
  if (!clientId || !invoiceId) return "/commissions-invoices";
  return `/commissions/edit-invoice/${clientId}/${invoiceId}`;
}

export function buildClientInvoiceEditPath(clientId, invoiceId) {
  if (!clientId || !invoiceId) return `/clients/client-invoices/${clientId}`;
  return `/clients/edit-invoice/${clientId}/${invoiceId}`;
}

/** URL slug (kebab) → API scope key for getStatusInvoiceFollowups */
const INVOICE_FOLLOWUP_SCOPE_URL_TO_API = {
  "invoice-client": "invoiceclient",
  "invoice-commission": "invoicecommission",
  "my-invoice-client": "myinvoiceclient",
  "my-invoice-commission": "myinvoicecommission",
};

const INVOICE_FOLLOWUP_SCOPE_API_TO_URL = Object.fromEntries(
  Object.entries(INVOICE_FOLLOWUP_SCOPE_URL_TO_API).map(([url, api]) => [
    api,
    url,
  ]),
);

export function resolveInvoiceFollowupScopeFromUrl(scopeSlug) {
  const raw = String(scopeSlug || "").trim();
  if (INVOICE_FOLLOWUP_SCOPE_URL_TO_API[raw]) {
    return INVOICE_FOLLOWUP_SCOPE_URL_TO_API[raw];
  }
  return raw;
}

export function toInvoiceFollowupScopeUrlSlug(scopeSlug) {
  const raw = String(scopeSlug || "").trim();
  if (INVOICE_FOLLOWUP_SCOPE_URL_TO_API[raw]) return raw;
  if (INVOICE_FOLLOWUP_SCOPE_API_TO_URL[raw]) {
    return INVOICE_FOLLOWUP_SCOPE_API_TO_URL[raw];
  }
  return raw;
}

export function isLegacyInvoiceFollowupScopeSlug(scopeSlug) {
  const raw = String(scopeSlug || "").trim();
  return Boolean(INVOICE_FOLLOWUP_SCOPE_API_TO_URL[raw]);
}

export function invoiceFollowupScopeTitle(scopeSlug) {
  const urlSlug = toInvoiceFollowupScopeUrlSlug(scopeSlug);
  const titles = {
    "invoice-client": "All users · Client invoice",
    "invoice-commission": "All users · Commission invoice",
    "my-invoice-client": "My queue · Client invoice",
    "my-invoice-commission": "My queue · Commission invoice",
  };
  return titles[urlSlug] || "Invoice follow-ups";
}

export function buildStatusInvoiceFollowupPath(ftype, scopeSlug, status) {
  const slug = toInvoiceFollowupScopeUrlSlug(scopeSlug);
  return `/status-invoice-followup/${ftype}/${slug}/${status}`;
}
